import { forwardRef, useCallback, type InputHTMLAttributes } from 'react';
import { format } from 'date-fns';
import { cn } from '@restworld/utils-common';

export type DatePickerValue = Date | string | null;

export interface DatePickerProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  /** Current date value (Date, ISO string, or null) */
  value?: DatePickerValue;
  /** Called with the new value string (yyyy-MM-dd) when the user picks a date */
  onChange?: (value: string) => void;
  /** Error / helper text shown below the input */
  helperText?: string;
  /** Test ID for data-cy attribute */
  testId?: string;
  /** Label displayed above the input */
  label?: string;
  /** Whether the input takes full width */
  isFullWidth?: boolean;
  /** Legacy prop - ignored (single date only) */
  selectRange?: boolean;
  /** Legacy border color hint */
  borderColor?: string;
  /** @deprecated Legacy prop - field validation state */
  fieldState?: string;
  /** @deprecated Legacy prop - ignored */
  locale?: string;
  /** @deprecated Legacy prop - ignored */
  maxDate?: Date;
  /** @deprecated Legacy prop - ignored */
  minDate?: Date;
  /** @deprecated Legacy prop - ignored */
  disableCalendar?: boolean;
}

/**
 * A minimal date picker built on the native date input.
 * Styled following shadcn/ui conventions with Tailwind utilities.
 */
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      helperText,
      testId,
      label,
      isFullWidth,
      disabled,
      className,
      id,
      // consumed but not forwarded
      selectRange: _selectRange,
      borderColor: _borderColor,
      fieldState: _fieldState,
      locale: _locale,
      maxDate: _maxDate,
      minDate: _minDate,
      disableCalendar: _disableCalendar,
      ...rest
    },
    ref
  ) => {
    const formattedValue = formatDateValue(value ?? null);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(e.target.value);
      },
      [onChange]
    );

    return (
      <div className={cn('flex flex-col gap-1.5', isFullWidth && 'w-full', className)}>
        {label ? (
          <label htmlFor={id} className={cn('text-sm font-medium text-neutral-700', disabled && 'text-neutral-300')}>
            {label}
          </label>
        ) : null}
        <input
          ref={ref}
          id={id}
          type="date"
          value={formattedValue}
          onChange={handleChange}
          disabled={disabled}
          data-cy={testId}
          className={cn(
            'h-12 rounded-lg border border-neutral-90 bg-neutral-10 px-3 py-2 text-base',
            'placeholder:text-neutral-200',
            'hover:bg-neutral-30',
            'focus:border-neutral-90 focus:border-2 focus:outline-none focus:ring-0',
            'disabled:cursor-not-allowed disabled:bg-neutral-40 disabled:opacity-70',
            helperText && 'border-red-500 bg-red-50'
          )}
          {...rest}
        />
        {helperText ? <p className="text-sm font-normal text-red-600">{helperText}</p> : null}
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';

/** Safely format a Date, ISO string, or null into yyyy-MM-dd for the native input */
function formatDateValue(value: DatePickerValue): string {
  if (!value) return '';
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '';
    return format(date, 'yyyy-MM-dd');
  } catch {
    return '';
  }
}
